import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Compass, ArrowRight } from 'lucide-react';
import { WELCOME } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { registerLearner } from '@/utils/api';

const Welcome = () => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleStart = async () => {
    if (!name.trim()) return;
    setLoading(true);
    sessionStorage.setItem('studentName', name.trim());
    await registerLearner(name.trim());
    setLoading(false);
    navigate('/dashboard');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleStart();
    }
  };

  return (
    <div 
      data-testid={WELCOME.container}
      className="h-screen flex items-center justify-center relative overflow-hidden bg-[#050505]"
    >
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1777380801981-0aa3ad2587e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwY2luZW1hdGljJTIwYWJzdHJhY3QlMjBwYXJ0aWNsZXMlMjBnb2xkJTIwYmx1ZXxlbnwwfHx8fDE3ODUxNzU1NDd8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.2,
          mixBlendMode: 'screen'
        }}
      />
      <div className="absolute inset-0 bg-black/80 z-0" />

      {/* Animated Particles */}
      <motion.div 
        className="absolute top-20 left-20 w-2 h-2 bg-[#FFB800] rounded-full"
        animate={{
          y: [0, -30, 0],
          opacity: [0.3, 1, 0.3]
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div 
        className="absolute bottom-32 right-32 w-3 h-3 bg-[#007AFF] rounded-full"
        animate={{
          y: [0, 30, 0],
          opacity: [0.3, 1, 0.3]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      />

      {/* Main Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 backdrop-blur-xl bg-black/60 border border-white/10 rounded-2xl p-12 w-full max-w-md shadow-[0_0_50px_rgba(255,184,0,0.1)]"
      >
        <div className="flex flex-col items-center gap-8">
          {/* Logo */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FFB800] to-[#FF8800] flex items-center justify-center shadow-[0_0_30px_rgba(255,184,0,0.4)]"
          >
            <Compass className="w-10 h-10 text-white" />
          </motion.div>

          {/* Title */}
          <div className="text-center space-y-2">
            <h1 className="font-heading text-4xl font-bold tracking-tight text-white">
              DSA With Kapil
            </h1>
            <p className="text-[#FFB800] font-heading text-lg font-medium">
              Visual Learning Lab
            </p>
          </div>

          {/* Input */}
          <div className="w-full space-y-4">
            <Input
              data-testid={WELCOME.nameInput}
              type="text"
              placeholder="Enter Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full bg-[#0F0F12] border-white/20 text-white placeholder:text-[#71717A] h-12 text-center font-body focus:border-[#FFB800] focus:ring-[#FFB800]/20 transition-colors"
            />

            <Button
              data-testid={WELCOME.startButton}
              onClick={handleStart}
              disabled={!name.trim() || loading}
              className="w-full h-12 bg-gradient-to-r from-[#FFB800] to-[#FF8800] hover:from-[#FFC520] hover:to-[#FF9920] text-black font-heading font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-[0_4px_20px_rgba(255,184,0,0.3)] hover:shadow-[0_6px_30px_rgba(255,184,0,0.5)]"
            >
              {loading ? 'Setting up...' : (<>
                Start Learning
                <ArrowRight className="ml-2 w-5 h-5" />
              </>)}
            </Button>
          </div>

          {/* Info Text */}
          <p className="text-[#A1A1AA] text-sm font-body text-center">
            No signup required • Learn by visualization
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Welcome;
